/* eslint-disable jsx-a11y/no-autofocus */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo, useState } from 'react';
import { CircularProgress, Popper, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import {Autocomplete} from '@material-ui/lab';
import { localize } from 'commons/LocalizedText/LocalizedText';
import * as PropTypes from 'prop-types';
import { MOBILE_BREAKPOINT } from 'utils/constants/responsive';
import { AUTOCOMPLETE_CATEGORIES } from 'utils/constants/autocompleteCategories';

const useStyles = makeStyles((theme) => ({
  root: (isMultiple) => ({
    flexGrow: 1,
    width: isMultiple ? 300 : 150,
    marginLeft: theme.spacing(3),
    marginRight: theme.spacing(3),
    [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
      marginLeft: theme.spacing(1),
      marginRight: theme.spacing(1)
    },
    '& .MuiTextField-root': {
      [theme.breakpoints.down(MOBILE_BREAKPOINT)]: {
        marginTop: theme.spacing(1),
        marginBottom: 0
      }
    }
  }),
  inputRoot: {
    '& .MuiAutocomplete-tagSizeSmall': {
      maxWidth: 'calc(40% - 4px)'
    }
  },
  input: {
    color: 'white'
  },
  titleOption: {
    color: `${theme.palette.primary.main} !important`,
    margin: 0
  },
  popper: {
    '& .MuiAutocomplete-option[aria-disabled="true"]': {
      opacity: 1,
      marginBottom: 0
    }
  },
  modified: {
    '& fieldset': {
      borderColor: '#31A597',
      borderWidth: '2px'
    }
  }
}));

/**
 * AutoComplete is a multi-select list; selected value(s) is(are) synchronized with the query string
 * @param options list of available options
 * @param label translated label of the field
 * @param isFree is selection of items not in the list possible
 * @param isMultiple is the field accepting multiple selects
 * @param className is used to apply style
 * @param isTranslatingOptions function to translate options if needed
 * @param isLoading do the data are loading
 * @param value value to display
 * @param onChange is the function triggered when the value changes
 * @param onChangeTextField is the function triggered when the textfield value changes
 * @param enableColorChangeOnModification enable the color changement when the value is changed
 */
export const AutoCompleteWithCategories = ({
  options, label, isFree, isMultiple, className, value, onChange, isLoading, onChangeTextField, searchString,
  enableColorChangeOnModification, autofocus
}) => {
  const classes = useStyles(isMultiple);
  const [isModified, setIsModified] = useState(false);

  const dynamicValues = useMemo(() => Object.values(options), [options]);

  const handleOnChangeTextField = (value) => {
    onChangeTextField(value);
    if(enableColorChangeOnModification) setIsModified(true);
  }

  const handleOnChange = (event, value) => {
    onChange(value);
    if(enableColorChangeOnModification) setIsModified(true);
  }

  return (
    <Autocomplete
      classes={{
        root: classes.root,
        inputRoot: classes.inputRoot
      }}
      className={`${classes.root} ${className}`}
      filterOptions={(options, _ref) => {
        const result = [];
        const keys = Object.keys(options);
        keys.forEach(key => {
          result.push({label: AUTOCOMPLETE_CATEGORIES[key].label, isTitle: true},...options[key]);
        });
        return result;
      }}
      freeSolo={isFree}
      getOptionDisabled={option => option.isTitle}
      getOptionLabel={option => option.label}
      getOptionSelected={(options, val) => value.label === val.label}
      loading={isLoading}
      loadingText={localize('loadingText')}
      noOptionsText={localize('noOptionsText')}
      onChange={handleOnChange}
      options={dynamicValues}
      PopperComponent={props =><Popper {...props} className={`${props.className} ${classes.popper}`}/>}
      renderInput={(params) => (
        <TextField
          {...params}
          autoFocus={autofocus}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? <div style={{marginRight: '25px', color:'white'}}><CircularProgress color='inherit' size={20} /></div> : null}
                {params.InputProps.endAdornment}
              </>
            ),
            className: `${classes.input} ${params.InputProps.className} ${isModified && classes.modified}`
          }}
          label={localize(label)}
          margin="normal"
          onChange={e => handleOnChangeTextField(e.target.value)}
        />
      )}
      renderOption={option => {
        return (option.isTitle ?
          <h4 className={classes.titleOption}>{localize(option.label)}</h4>
          :
          option.isSubTitle ?
            <div style={{paddingLeft: '10px'}}>{localize(option.label)}</div>
            :
            <Typography
              dangerouslySetInnerHTML={{
                __html: option.label?.toUpperCase().replace(new RegExp(searchString, "ig"),
                  searchString.toUpperCase().bold())
              }}
              style={{paddingLeft: '10px'}}
            />
        )
      }}
      size="medium"
      value={value}
    />
  );
};

AutoCompleteWithCategories.propTypes = {
  className: PropTypes.string,
  isFree: PropTypes.bool,
  isMultiple: PropTypes.bool,
  isTranslatingOptions: PropTypes.bool,
  label: PropTypes.string.isRequired,
  onChangeTextField: PropTypes.func,
  options: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object, PropTypes.string])), PropTypes.object])
};

AutoCompleteWithCategories.defaultProps = {
  className: '',
  isFree: false,
  isMultiple: false,
  isTranslatingOptions: false,
  onChangeTextField: () => {},
  options: []
};